import { z } from "zod";
//#region src/cp-audit/changes.ts
/**
* The before and after values of a CP update, as the audit row stores them in `details.changes`.
*
* Plan `2026-10-14-cp-audit-coverage`, decisions D-6 and D-22, defaults rows F-7 and F-12.
*
* Who calls it: every CP emit site that records an update. The caller reads the row before the
* write in the same transaction, writes, and then calls `auditChanges(before, after)` (F-7). The
* Audit page reads the result back with {@link readCpAuditChanges} and shows one table row per
* changed field.
*
* What it guarantees:
*
* 1. One entry per top-level field whose value differs, keyed by the field name, sorted by name.
*    An equal field has no entry, so an update that changes nothing gives `{}` (F-11 uses that).
* 2. A value under a secret key, at any depth, reads `'[redacted]'` (F-12). The helper is a
*    backstop: an emit site never passes a secret value on purpose. A change entry under a secret
*    key keeps its `{ from, to }` shape with each non-null side redacted, so the backstop that
*    cp-auth-api runs over `details` keeps `details.changes` readable.
* 3. A value whose JSON text is longer than 2 KB stores the first 2 KB of that text as a string,
*    and the entry carries `truncated: true` (D-22). The whole result stays under 12 KB, so it
*    always fits the 16 KB ingest cap on `details`; a larger result stores shorter starts.
* 4. Every stored value is plain JSON. A `Date` reads as its ISO string, a `bigint` as its decimal
*    string, and `undefined` as `null`.
* 5. It never throws, and it never reads an inherited property: a `__proto__` key compares as data.
*
* The module uses `TextEncoder` and no Node global, so a browser bundle may import it through the
* package root (#3073).
*/
/** The longest JSON text, in UTF-8 bytes, that one stored `from` or `to` value keeps (D-22). */
const CP_AUDIT_CHANGE_VALUE_MAX_BYTES = 2048;
/**
* The longest JSON text, in UTF-8 bytes, of one whole `auditChanges` result: 12 KB.
*
* The ingest caps all of `details` at 16 KB (`CP_AUDIT_DETAILS_MAX_BYTES`). `{ changes }` at this
* budget measures at most 12,300 bytes, so the other keys of `details` keep 4,084 bytes.
*/
const CP_AUDIT_CHANGES_MAX_BYTES = 12288;
/** The text that replaces a non-null value under a secret key. */
const CP_AUDIT_REDACTED = "[redacted]";
/** The text that replaces an object that contains itself. */
const CP_AUDIT_CIRCULAR = "[circular]";
/**
* The words that mark a key secret on their own, when they end the key.
*
* A key is split into lower-case words at `_`, `-`, `.`, spaces and case changes: `clientSecret`
* reads `client secret`, `x-api-key` reads `x api key`, `APIKey` reads `api key`. The rule reads
* the end of the key because it names what the value is: `refreshToken` is a token, `tokenTtl` is a
* duration. The rule also joins the last two or more words, so `kubeConfig` matches `kubeconfig`
* and `gcpServiceAccountJson` matches `serviceaccountjson`. The joined words also cover a key in
* one lower-case word, such as `apikey`.
*/
const CP_AUDIT_SECRET_KEY_NOUNS = [
	"accesstoken",
	"apikey",
	"apikeys",
	"authorization",
	"clientsecret",
	"cookie",
	"cookies",
	"credential",
	"credentials",
	"dockerconfig",
	"dockerconfigjson",
	"dsn",
	"kubeconfig",
	"passphrase",
	"passwd",
	"password",
	"pepper",
	"privatekey",
	"refreshtoken",
	"secret",
	"secretkey",
	"secrets",
	"serviceaccountjson",
	"token",
	"tokens",
	"webhookurl"
];
/**
* The last words that mark a key secret only after one of {@link CP_AUDIT_SECRET_KEY_QUALIFIERS}.
* `privateKeyPem`, `keyPem` and `apiKey` are secret; `publicKeyPem` and `bundleHash` are not.
*/
const CP_AUDIT_SECRET_KEY_QUALIFIED_NOUNS = [
	"der",
	"hash",
	"jwk",
	"json",
	"key",
	"keys",
	"pem",
	"string",
	"url"
];
/**
* The earlier words that make a word of {@link CP_AUDIT_SECRET_KEY_QUALIFIED_NOUNS} secret.
*
* The word `key` counts only in a key with no word `public`: `keyPem` is a private key, and
* `publicKeyPem` is not. Every other word counts in any key, so `sshPublicKey` stays secret.
*/
const CP_AUDIT_SECRET_KEY_QUALIFIERS = [
	"access",
	"api",
	"client",
	"connection",
	"database",
	"encryption",
	"key",
	"master",
	"password",
	"priv",
	"private",
	"secret",
	"session",
	"signing",
	"ssh",
	"token",
	"webhook"
];
/**
* The earlier words that make the last word `key` or `keys` secret, and no other word of
* {@link CP_AUDIT_SECRET_KEY_QUALIFIED_NOUNS}. `tlsKey`, `caKey` and the PagerDuty `routingKey` are
* secret; `caPem`, `caCertPem` and `integrationUrl` are not.
*/
const CP_AUDIT_SECRET_KEY_KEY_QUALIFIERS = [
	"account",
	"admin",
	"auth",
	"ca",
	"deploy",
	"hmac",
	"integration",
	"license",
	"root",
	"routing",
	"service",
	"shared",
	"tls"
];
const SECRET_NOUNS = new Set(CP_AUDIT_SECRET_KEY_NOUNS);
const QUALIFIED_NOUNS = new Set(CP_AUDIT_SECRET_KEY_QUALIFIED_NOUNS);
const QUALIFIERS = new Set(CP_AUDIT_SECRET_KEY_QUALIFIERS);
const KEY_QUALIFIERS = new Set(CP_AUDIT_SECRET_KEY_KEY_QUALIFIERS);
/** Split a key into lower-case words at separators and case changes. */
function keyWords(key) {
	return key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2").toLowerCase().split(/[^a-z0-9]+/).filter((word) => word !== "");
}
/**
* Tell whether a field name marks a secret value.
*
* Input: any key text. Output: `true` in each of these cases, and `false` otherwise:
*
* 1. `extraKeys` holds the key, compared without case.
* 2. The last word, or the last two or more words joined, is a word of
*    {@link CP_AUDIT_SECRET_KEY_NOUNS}.
* 3. The last word is a word of {@link CP_AUDIT_SECRET_KEY_QUALIFIED_NOUNS}, and an earlier word is
*    a word of {@link CP_AUDIT_SECRET_KEY_QUALIFIERS} (the word `key` only when no word is
*    `public`).
* 4. The last word is `key` or `keys`, and an earlier word is a word of
*    {@link CP_AUDIT_SECRET_KEY_KEY_QUALIFIERS}.
*
* It never throws.
*/
function isCpAuditSecretKey(key, extraKeys = []) {
	const lowered = key.toLowerCase();
	if (extraKeys.some((extra) => extra.toLowerCase() === lowered)) return true;
	const words = keyWords(key);
	const last = words[words.length - 1];
	if (last === void 0) return false;
	for (let start = 0; start < words.length; start += 1) if (SECRET_NOUNS.has(words.slice(start).join(""))) return true;
	const earlier = words.slice(0, -1);
	const isPublic = words.includes("public");
	if (QUALIFIED_NOUNS.has(last) && earlier.some((word) => QUALIFIERS.has(word) && !(word === "key" && isPublic))) return true;
	return (last === "key" || last === "keys") && earlier.some((word) => KEY_QUALIFIERS.has(word));
}
/** Read an own property, never an inherited one. */
function ownValue(record, key) {
	return Object.hasOwn(record, key) ? record[key] : void 0;
}
/** Build an object from entries as own data properties, so a `__proto__` key stays data. */
function objectOf(entries) {
	const out = {};
	for (const [key, value] of entries) Object.defineProperty(out, key, {
		value,
		enumerable: true,
		writable: true,
		configurable: true
	});
	return out;
}
/**
* Tell whether a value has the shape of a {@link CpAuditChange}: an object, not an array, with own
* `from` and `to` keys, no other own key except `truncated`, and `truncated` absent or `true`.
*/
function isChangeEntry(value) {
	if (Array.isArray(value) || !Object.hasOwn(value, "from") || !Object.hasOwn(value, "to")) return false;
	if (!Object.keys(value).every((key) => key === "from" || key === "to" || key === "truncated")) return false;
	return !Object.hasOwn(value, "truncated") || ownValue(value, "truncated") === true;
}
/**
* The stored form of a value under a secret key. A `null` stays `null`.
*
* A value with the shape of a change entry keeps that shape: each non-null side reads
* {@link CP_AUDIT_REDACTED}, a `null` or absent side reads `null`, and `truncated: true` stays. So
* the backstop over `details` keeps every entry of `details.changes` readable, and the trail still
* says that the secret changed (D11). Any other value reads {@link CP_AUDIT_REDACTED} whole.
*/
function redactedSecret(value) {
	if (value === null) return null;
	if (typeof value !== "object" || !isChangeEntry(value)) return CP_AUDIT_REDACTED;
	const side = (item) => item === null || item === void 0 ? null : CP_AUDIT_REDACTED;
	const entries = [["from", side(value.from)], ["to", side(value.to)]];
	if (value.truncated === true) entries.push(["truncated", true]);
	return objectOf(entries);
}
/**
* Turn any value into plain JSON, with secret keys redacted when `redact` is set.
*
* `seen` holds the objects on the current path, so a cycle reads {@link CP_AUDIT_CIRCULAR} and a
* shared object that is not a cycle reads in full at each place.
*/
function toJsonValue(value, redact, secretKeys, seen) {
	if (value === null || value === void 0) return null;
	switch (typeof value) {
		case "string":
		case "boolean": return value;
		case "number": return Number.isFinite(value) ? value : null;
		case "bigint": return value.toString();
		case "object": break;
		default: return null;
	}
	const object = value;
	if (seen.has(object)) return CP_AUDIT_CIRCULAR;
	const toJson = object.toJSON;
	if (typeof toJson === "function") {
		let converted;
		try {
			converted = toJson.call(object);
		} catch {
			return null;
		}
		seen.add(object);
		const result = converted === object ? null : toJsonValue(converted, redact, secretKeys, seen);
		seen.delete(object);
		return result;
	}
	seen.add(object);
	let result;
	if (Array.isArray(object)) result = object.map((item) => toJsonValue(item, redact, secretKeys, seen));
	else {
		const entries = [];
		for (const key of Object.keys(object).sort()) {
			const item = ownValue(object, key);
			if (item === void 0) continue;
			entries.push([key, redact && isCpAuditSecretKey(key, secretKeys) ? redactedSecret(item) : toJsonValue(item, redact, secretKeys, seen)]);
		}
		result = objectOf(entries);
	}
	seen.delete(object);
	return result;
}
/**
* Redact every secret key in a value, at any depth, and return the value as plain JSON.
*
* Who calls it: cp-auth-api on each `details` object before the insert, as the backstop of F-12,
* and {@link auditChanges} on each stored value. Input: any value. Output: the JSON form of the
* value (guarantee 4 of the module), with each non-null value under a secret key replaced by
* {@link CP_AUDIT_REDACTED}. A `null` under a secret key stays `null`: it says the secret was unset
* and reveals nothing. Object keys come back sorted. It never throws and never changes its input.
*
* A value under a secret key that has the shape of a change entry (`{ from, to, truncated? }`)
* keeps that shape, with each non-null side redacted. So the backstop over the output of
* {@link auditChanges} changes nothing, and {@link readCpAuditChanges} reads the stored row back.
*/
function redactCpAuditValue(value, opts = {}) {
	return toJsonValue(value, true, opts.secretKeys ?? [], /* @__PURE__ */ new WeakSet());
}
const utf8Encoder = new TextEncoder();
/**
* The length in UTF-8 bytes of the JSON text of a value.
*
* Who calls it: the ingest schema for the 16 KB cap on `details`, and {@link auditChanges} for the
* 2 KB cap on one value. Input: any value. Output: the byte length of the JSON text of the JSON
* form of the value (guarantee 4 of the module). It never throws.
*/
function cpAuditJsonByteLength(value) {
	return utf8Encoder.encode(JSON.stringify(toJsonValue(value, false, [], /* @__PURE__ */ new WeakSet()))).length;
}
/**
* The UTF-8 size of one character of a `for...of` walk. A surrogate pair is 4 bytes; a lone
* surrogate encodes as U+FFFD, 3 bytes.
*/
function utf8Size(char) {
	if (char.length === 2) return 4;
	const code = char.charCodeAt(0);
	if (code < 128) return 1;
	return code < 2048 ? 2 : 3;
}
/**
* Cut a text to at most `maxBytes` UTF-8 bytes, on a character boundary.
* A character of 2 to 4 bytes that would cross the limit is dropped whole.
*/
function cutUtf8(text, maxBytes) {
	let bytes = 0;
	let end = 0;
	for (const char of text) {
		const size = utf8Size(char);
		if (bytes + size > maxBytes) break;
		bytes += size;
		end += char.length;
	}
	return text.slice(0, end);
}
function pendingSide(value) {
	const text = JSON.stringify(value);
	return {
		value,
		text,
		bytes: utf8Encoder.encode(text).length
	};
}
/** The UTF-8 size of the mark that a cut adds to its entry: `,"truncated":true`, 17 bytes. */
const TRUNCATED_MARK_BYTES = utf8Encoder.encode(",\"truncated\":true").length;
/**
* One stored value: the JSON value, or the first `maxBytes` of its JSON text when it is longer.
*
* A side stays whole in three cases (R-002):
*
* 1. Its JSON text is at most `maxBytes`.
* 2. It is `null`, a boolean or a number. Such a value is at most 25 bytes of JSON text, and a cut
*    would hide it for no gain.
* 3. Its JSON text is not longer than its cut form, the JSON text of the cut string plus the
*    17-byte truncation mark, and not longer than {@link CP_AUDIT_CHANGE_VALUE_MAX_BYTES}. So a short
*    string, and {@link CP_AUDIT_REDACTED}, stay whole at every cap, and no side passes 2 KB (D-22).
*
* So `null` and `'[redacted]'` sides never change, and the backstop over the result changes
* nothing (D11).
*/
function capped(side, maxBytes) {
	const whole = {
		value: side.value,
		truncated: false
	};
	if (side.bytes <= maxBytes || side.value === null || typeof side.value === "boolean" || typeof side.value === "number") return whole;
	const cut = cutUtf8(side.text, maxBytes);
	const cutBytes = utf8Encoder.encode(JSON.stringify(cut)).length;
	if (side.bytes <= 2048 && side.bytes <= cutBytes + TRUNCATED_MARK_BYTES) return whole;
	return {
		value: cut,
		truncated: true
	};
}
/**
* Compute the `details.changes` object of an update.
*
* Inputs:
*
* - `before`: the row as read before the write, in the same transaction (F-7). `null` or
*   `undefined` for a create: every field of `after` then reads `from: null`.
* - `after`: the row as written. `null` or `undefined` for a delete: every field of `before` then
*   reads `to: null`.
* - `opts`: see {@link AuditChangesOptions}.
*
* Output: one {@link CpAuditChange} per top-level field whose JSON form differs between the two
* sides, sorted by field name. A field absent on one side compares as `null`, so absent and `null`
* are equal. Two objects compare by their JSON form with sorted keys, so key order never counts as
* a change. The comparison reads the values before redaction, so a changed secret still gives an
* entry, with `'[redacted]'` on each non-null side. An entry is never more than 2 KB per side
* (D-22). It never throws and never changes its inputs.
*
* The whole result is never more than {@link CP_AUDIT_CHANGES_MAX_BYTES} as JSON text, so the
* ingest cap of 16 KB on `details` accepts it. When the entries at 2 KB per side pass that budget,
* the helper lowers the cap on each side, the same cap for every value, until the result fits. A
* value that fits under the lower cap stays whole; a longer value stores the start of its JSON text
* and its entry carries `truncated: true`. A `null`, a boolean, a number and a value no longer than
* its cut form stay whole at every cap (R-002). When even the smallest form of every entry passes
* the budget (several hundred changed fields), the helper keeps each entry in name order that still
* fits, in that smallest form, and leaves out the rest. The result is then never empty while one
* entry fits.
*/
function auditChanges(before, after, opts = {}) {
	const secretKeys = opts.secretKeys ?? [];
	const ignore = new Set(opts.ignore ?? []);
	const keys = opts.fields !== void 0 ? new Set(opts.fields) : /* @__PURE__ */ new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
	const pending = [];
	for (const key of [...keys].sort()) {
		if (ignore.has(key)) continue;
		const fromRaw = before == null ? void 0 : ownValue(before, key);
		const toRaw = after == null ? void 0 : ownValue(after, key);
		const fromPlain = toJsonValue(fromRaw, false, secretKeys, /* @__PURE__ */ new WeakSet());
		const toPlain = toJsonValue(toRaw, false, secretKeys, /* @__PURE__ */ new WeakSet());
		if (JSON.stringify(fromPlain) === JSON.stringify(toPlain)) continue;
		const secret = isCpAuditSecretKey(key, secretKeys);
		pending.push({
			key,
			from: pendingSide(secret && fromPlain !== null ? CP_AUDIT_REDACTED : redactCpAuditValue(fromRaw, opts)),
			to: pendingSide(secret && toPlain !== null ? CP_AUDIT_REDACTED : redactCpAuditValue(toRaw, opts))
		});
	}
	const entryAt = (item, maxBytes) => {
		const from = capped(item.from, maxBytes);
		const to = capped(item.to, maxBytes);
		return from.truncated || to.truncated ? {
			from: from.value,
			to: to.value,
			truncated: true
		} : {
			from: from.value,
			to: to.value
		};
	};
	const build = (maxBytes) => objectOf(pending.map((item) => [item.key, entryAt(item, maxBytes)]));
	const fits = (changes) => utf8Encoder.encode(JSON.stringify(changes)).length <= CP_AUDIT_CHANGES_MAX_BYTES;
	const full = build(CP_AUDIT_CHANGE_VALUE_MAX_BYTES);
	if (fits(full)) return full;
	let fitting = build(0);
	if (fits(fitting)) {
		let low = 0;
		let high = CP_AUDIT_CHANGE_VALUE_MAX_BYTES;
		while (high - low > 1) {
			const middle = Math.floor((low + high) / 2);
			const attempt = build(middle);
			if (fits(attempt)) {
				low = middle;
				fitting = attempt;
			} else high = middle;
		}
		return fitting;
	}
	const kept = [];
	let used = 2;
	for (const item of pending) {
		const entry = entryAt(item, 0);
		const size = utf8Encoder.encode(`${JSON.stringify(item.key)}:${JSON.stringify(entry)}`).length + (kept.length > 0 ? 1 : 0);
		if (used + size > 12288) continue;
		kept.push([item.key, entry]);
		used += size;
	}
	return objectOf(kept);
}
/** The zod form of one JSON value. */
const cpAuditJsonValueSchema = z.json();
/** The zod form of one {@link CpAuditChange}. Strict: an entry has no other key. */
const cpAuditChangeSchema = z.object({
	from: cpAuditJsonValueSchema,
	to: cpAuditJsonValueSchema,
	truncated: z.literal(true).optional()
}).strict();
/** The zod form of a `details.changes` object. */
const cpAuditChangesSchema = z.record(z.string(), cpAuditChangeSchema);
/**
* Read the `details.changes` object of a stored row.
*
* Who calls it: the Audit page, to build the before and after table of a row. Input: the `details`
* of a row, any value. Output: the changes when `details.changes` parses with
* {@link cpAuditChangesSchema}; `null` when `details` is not an object, holds no `changes`, or holds
* a `changes` of another shape. A row written before this plan holds new values only, so it reads
* `null` and the page shows its details as written. It never throws.
*/
function readCpAuditChanges(details) {
	if (typeof details !== "object" || details === null || Array.isArray(details)) return null;
	const parsed = cpAuditChangesSchema.safeParse(ownValue(details, "changes"));
	return parsed.success ? parsed.data : null;
}
//#endregion
export { CP_AUDIT_CHANGES_MAX_BYTES, CP_AUDIT_CHANGE_VALUE_MAX_BYTES, CP_AUDIT_CIRCULAR, CP_AUDIT_REDACTED, CP_AUDIT_SECRET_KEY_KEY_QUALIFIERS, CP_AUDIT_SECRET_KEY_NOUNS, CP_AUDIT_SECRET_KEY_QUALIFIED_NOUNS, CP_AUDIT_SECRET_KEY_QUALIFIERS, auditChanges, cpAuditChangeSchema, cpAuditChangesSchema, cpAuditJsonByteLength, cpAuditJsonValueSchema, isCpAuditSecretKey, readCpAuditChanges, redactCpAuditValue };

//# sourceMappingURL=changes.js.map